import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mv-root" }
const _hoisted_2 = { class: "mv-header" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "mv-header-info" }
const _hoisted_5 = { class: "mv-header-name" }
const _hoisted_6 = { class: "mv-header-role" }
const _hoisted_7 = {
  key: 0,
  class: "mv-stale-bar"
}
const _hoisted_8 = { class: "mv-search-wrap" }
const _hoisted_9 = { class: "mv-tabs" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "mv-tab-count" }
const _hoisted_12 = { class: "mv-list" }
const _hoisted_13 = {
  key: 0,
  class: "mv-state"
}
const _hoisted_14 = {
  key: 1,
  class: "mv-state"
}
const _hoisted_15 = ["onClick"]
const _hoisted_16 = ["src"]
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "mv-item-body" }
const _hoisted_19 = { class: "mv-item-top" }
const _hoisted_20 = { class: "mv-item-name" }
const _hoisted_21 = { class: "mv-item-sub" }
const _hoisted_22 = {
  key: 0,
  class: "mv-item-tags"
}

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import FocoChatPanel from '@/components/dashboard/FocoChatPanel.vue'
import { useWootDashLiveData, startWootDashLiveData, stopWootDashLiveData } from '@/composables/useWootDashLiveData'
import { pendingChatId, currentOpenConvId } from '@/composables/useChatTrigger'
import { avatarColor, initials } from '@/composables/useHelpers'
import { fmtPhone } from '@/utils/contactsFormat'
import { useLogo } from '@/composables/useLogo'
import { performLogout } from '@/composables/useAutoLogout'

type Tab = 'mine' | 'queue' | 'all'

export default /*@__PURE__*/_defineComponent({
  __name: 'MobileView',
  setup(__props) {

// Tela de celular no WootDash (substitui a antiga, que buscava conversas/mensagens/macros
// no n8n). Lista = os mesmos dados ao vivo do Dashboard (useWootDashLiveData: SignalR +
// lista negra + fluxos escondidos); conversa = o chat do Modo Foco (FocoChatPanel) em tela cheia.
// Avisos de atribuição e em massa já vêm do App.vue (SignalR), iguais ao desktop.
const store = useStore()
const router = useRouter()
const { logoUrl } = useLogo()
const authUser = computed(() => store.getters.authUser)
const token = computed<string>(() => authUser.value?.wootdash_token ?? '')
const myAgentId = computed<string>(() => authUser.value?.wootdash_agent_id ?? '')

const { conversations, scopeReady, dataStale } = useWootDashLiveData()

const tab = ref<Tab>('mine')
const query = ref('')
const openId = ref<string | null>(null)
const nowSec = ref(Math.floor(Date.now() / 1000))

const mine = computed(() => conversations.value.filter(c => c.assignee?.id === myAgentId.value))
const queue = computed(() => conversations.value.filter(c => !c.assignee))
const myCount = computed(() => mine.value.length)

const tabs = computed(() => [
  { key: 'mine' as Tab, label: 'Minhas', count: mine.value.length },
  { key: 'queue' as Tab, label: 'Sem agente', count: queue.value.length },
  { key: 'all' as Tab, label: 'Todas', count: conversations.value.length },
])

const visible = computed(() => {
  const base = tab.value === 'mine' ? mine.value : tab.value === 'queue' ? queue.value : conversations.value
  const q = query.value.trim().toLowerCase()
  const digits = q.replace(/\D/g, '')
  const filtered = q
    ? base.filter(c => c.contact.name.toLowerCase().includes(q) || (digits.length >= 3 && c.contact.phone.replace(/\D/g, '').includes(digits)))
    : base
  // Quem espera há mais tempo primeiro; sem espera, mais recentes depois.
  return [...filtered].sort((a, b) => (a.waiting_since || Infinity) - (b.waiting_since || Infinity) || b.created_at - a.created_at)
})

function waitLabel(since: number): string {
  const min = Math.max(0, Math.floor((nowSec.value - since) / 60))
  if (min < 1) return 'agora'
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  return h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`
}

function openChat(id: string) {
  openId.value = id
  currentOpenConvId.value = id
}

function closeChat() {
  openId.value = null
  currentOpenConvId.value = null
}

// "Abrir" no aviso de atribuição (ou notificação) — mesmo sinal que o ConvDrawer escuta no desktop.
watch(pendingChatId, id => {
  if (id === null) return
  openChat(String(id))
  pendingChatId.value = null
}, { immediate: true })

function restart() {
  if (token.value) startWootDashLiveData(token.value)
}

async function handleLogout() {
  await performLogout(store, router)
}

let nowTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  restart()
  nowTimer = setInterval(() => { nowSec.value = Math.floor(Date.now() / 1000) }, 30_000)
})
onUnmounted(() => {
  stopWootDashLiveData()
  if (nowTimer) clearInterval(nowTimer)
  currentOpenConvId.value = null
})
watch(token, t => { if (t) restart() })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!openId.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("header", _hoisted_2, [
            _createElementVNode("img", {
              src: _unref(logoUrl),
              class: "mv-logo",
              alt: "ISBET"
            }, null, 8, _hoisted_3),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(authUser.value?.name), 1),
              _createElementVNode("span", _hoisted_6, _toDisplayString(myCount.value) + " conversa" + _toDisplayString(myCount.value === 1 ? '' : 's') + " com você", 1)
            ]),
            _createElementVNode("button", {
              class: "mv-icon-btn",
              title: "Sair",
              onClick: handleLogout
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("svg", {
                width: "18",
                height: "18",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "2.2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              }, [
                _createElementVNode("path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" }),
                _createElementVNode("polyline", { points: "16 17 21 12 16 7" }),
                _createElementVNode("line", {
                  x1: "21",
                  y1: "12",
                  x2: "9",
                  y2: "12"
                })
              ], -1)
            ]))])
          ]),
          (_unref(dataStale))
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _cache[3] || (_cache[3] = _createTextVNode(" Sem conexão — dados desatualizados ", -1)),
                _createElementVNode("button", {
                  class: "mv-stale-btn",
                  onClick: restart
                }, "Tentar")
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            _cache[4] || (_cache[4] = _createElementVNode("svg", {
              class: "mv-search-ico",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round"
            }, [
              _createElementVNode("circle", {
                cx: "11",
                cy: "11",
                r: "8"
              }),
              _createElementVNode("line", {
                x1: "21",
                y1: "21",
                x2: "16.65",
                y2: "16.65"
              })
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
              class: "mv-search",
              type: "search",
              placeholder: "Buscar por nome ou telefone…"
            }, null, 512), [
              [_vModelText, query.value]
            ])
          ]),
          _createElementVNode("nav", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (t) => {
              return (_openBlock(), _createElementBlock("button", {
                key: t.key,
                class: _normalizeClass(["mv-tab", { active: tab.value === t.key }]),
                onClick: ($event: any) => (tab.value = t.key)
              }, [
                _createTextVNode(_toDisplayString(t.label) + " ", 1),
                _createElementVNode("span", _hoisted_11, _toDisplayString(t.count), 1)
              ], 10, _hoisted_10))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_12, [
            (!_unref(scopeReady))
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[5] || (_cache[5] = [
                  _createElementVNode("span", { class: "mv-spinner" }, null, -1)
                ]))]))
              : (!visible.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(query.value ? 'Nenhuma conversa encontrada.' : tab.value === 'mine' ? 'Nenhuma conversa ativa com você.' : 'Nada por aqui.'), 1))
                : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visible.value, (c) => {
              return (_openBlock(), _createElementBlock("button", {
                key: c.id,
                class: "mv-item",
                onClick: ($event: any) => (openChat(c.id))
              }, [
                _createElementVNode("span", {
                  class: "mv-av",
                  style: _normalizeStyle({ background: _unref(avatarColor)(c.contact.name) })
                }, [
                  (c.contact.thumbnail)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: c.contact.thumbnail,
                        alt: "",
                        onError: _cache[1] || (_cache[1] = (e) => (e.target as HTMLImageElement).style.display = 'none')
                      }, null, 40, _hoisted_16))
                    : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_unref(initials)(c.contact.name)), 1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["mv-dot", c.status])
                  }, null, 2)
                ], 4),
                _createElementVNode("span", _hoisted_18, [
                  _createElementVNode("span", _hoisted_19, [
                    _createElementVNode("span", _hoisted_20, _toDisplayString(c.contact.name || _unref(fmtPhone)(c.contact.phone)), 1),
                    (c.waiting_since)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: _normalizeClass(["mv-wait", { late: nowSec.value - c.waiting_since > 15 * 60 }])
                        }, _toDisplayString(waitLabel(c.waiting_since)), 3))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("span", _hoisted_21, [
                    (c.fluxo_id)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(c.fluxo_id), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (c.team)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(" · " + _toDisplayString(c.team.name), 1)
                        ], 64))
                      : _createCommentVNode("", true),
                    (tab.value !== 'mine')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createTextVNode(" · " + _toDisplayString(c.assignee?.name || 'Sem agente'), 1)
                        ], 64))
                      : _createCommentVNode("", true)
                  ]),
                  (c.labels.length)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_22, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(c.labels.slice(0, 3), (l) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: l,
                            class: "mv-tag"
                          }, _toDisplayString(l), 1))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ])
              ], 8, _hoisted_15))
            }), 128))
          ])
        ], 64))
      : (_openBlock(), _createBlock(FocoChatPanel, {
          key: 1,
          "conversation-id": openId.value,
          mobile: "",
          class: "mv-chat",
          onClose: closeChat
        }, null, 8, ["conversation-id"]))
  ]))
}
}

})